import './styles.css';
import { GameApp } from './app/game-app';
import { ActionAudio } from './audio/action-audio';
import { resolveSessionConfig } from './configuration/session-config';
import { SelfieSprintSimulation } from './core/simulation';
import type { GameCommand } from './core/types';
import { DebugPanel } from './debug/debug-panel';
import { PointerKeyboardInput } from './input/pointer-keyboard-input';
import { BrowserClock } from './platform/browser-clock';
import { ConsoleLogger } from './platform/console-logger';
import { supportsWebGl2, ThreeGameView } from './rendering/three-game-view';
import { applyVisualPreset } from './rendering/visual-presets';
import { GameUi } from './ui/game-ui';
import { PresetLab } from './ui/preset-lab';

function seedFromUrl(): string {
  const requested = new URLSearchParams(window.location.search).get('seed');
  if (requested !== null && requested.trim() !== '') return requested.trim();
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1_000_000).toString(36)}`;
}

const root = document.querySelector<HTMLElement>('#app');
if (root === null) throw new Error('Missing #app root');

const sessionConfig = resolveSessionConfig(window.location.search);
applyVisualPreset(document.documentElement, sessionConfig.visual);
document
  .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  ?.setAttribute('content', sessionConfig.visual.ui.page);

const logger = new ConsoleLogger();
for (const invalid of sessionConfig.invalidRequests) {
  logger.info('unknown preset requested; using fallback', {
    parameter: invalid.parameter,
    requested: invalid.requested,
    fallback: invalid.fallback,
  });
}

const simulation = new SelfieSprintSimulation(sessionConfig.physics.config);
simulation.reset(seedFromUrl());

let app: GameApp | undefined;
const pendingCommands: GameCommand[] = [];
const feedback = new ActionAudio();
const ui = new GameUi(
  root,
  (command) => {
    if (app === undefined) pendingCommands.push(command);
    else app.dispatch(command);
  },
  {
    isMuted: () => feedback.isMuted(),
    toggle: () => {
      const muted = !feedback.isMuted();
      feedback.setMuted(muted);
      return muted;
    },
  },
);
new PresetLab(root, sessionConfig);

if (!supportsWebGl2()) {
  feedback.dispose();
  ui.showUnsupported();
  logger.error('WebGL 2 is unavailable');
} else {
  const input = new PointerKeyboardInput(ui.canvas);
  const view = new ThreeGameView(ui.canvas, sessionConfig.physics.config, sessionConfig.visual);
  const debug = new DebugPanel(ui.debugElement(), sessionConfig.debugEnabled, {
    physics: sessionConfig.physicsId,
    visual: sessionConfig.visualId,
  });
  app = new GameApp(
    simulation,
    view,
    input,
    ui,
    debug,
    new BrowserClock(),
    logger,
    feedback,
    sessionConfig.physics.config.fixedStep,
  );
  for (const command of pendingCommands) app.dispatch(command);
  app.start();

  if (import.meta.env.DEV || __ENABLE_TEST_API__) {
    const runningApp = app;
    window.__SELFIE_GAMES_TEST__ = {
      getSnapshot: () => simulation.getSnapshot(),
      restart: (seed) => {
        runningApp.restart(seed);
      },
      dispatch: (command) => {
        runningApp.dispatch(command);
      },
      getDiagnostics: () => runningApp.diagnostics(),
      getPresetSelection: () => ({
        physics: sessionConfig.physicsId,
        visual: sessionConfig.visualId,
      }),
    };
  }
}

window.addEventListener('error', (event) => {
  logger.error('uncaught browser error', event.error);
});
window.addEventListener('unhandledrejection', (event) => {
  logger.error('unhandled promise rejection', event.reason);
});
